/**
 * CryptoAuditCard — Immutable cryptographic audit card for a shipment.
 *
 * Displays the latest HMAC-SHA256 signature metadata (root hash,
 * signed_at, key_id) and lets the user re-verify integrity against
 * live Storage via the `verify-dossier` Edge Function (DIAN-03).
 */
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Fingerprint,
  ShieldCheck,
  ShieldAlert,
  FileCheck,
  Loader2,
  XCircle,
  CheckCircle2,
  Copy,
} from "lucide-react";
import { useLatestDossierSignature } from "@/hooks/useLatestDossierSignature";
import { useVerifyDossier } from "@/hooks/useVerifyDossier";
import { toast } from "sonner";

interface Props {
  embarqueId: string;
}

export function CryptoAuditCard({ embarqueId }: Props) {
  const { data: signature, isLoading } = useLatestDossierSignature(embarqueId);
  const { status, result, error, verify, isVerifying } = useVerifyDossier();

  const handleVerify = async () => {
    if (!signature) return;
    try {
      await verify(signature.id);
    } catch {
      /* surfaced inline via `error` */
    }
  };

  const copyHash = (hash: string) => {
    navigator.clipboard.writeText(hash);
    toast.success("Hash copiado al portapapeles");
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Fingerprint className="h-4 w-4 text-primary" />
          Auditoría Criptográfica (Inmutable)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground py-6 justify-center">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Consultando registro de firmas…
          </div>
        ) : !signature ? (
          // ── Empty state ──────────────────────────────────────────
          <div className="flex flex-col items-center justify-center py-8 text-center gap-3 border-2 border-dashed border-border rounded-lg bg-muted/20">
            <div className="h-12 w-12 rounded-full bg-muted/40 flex items-center justify-center">
              <ShieldAlert className="h-6 w-6 text-muted-foreground" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-foreground">Dossier sin sellar</p>
              <p className="text-[11px] text-muted-foreground max-w-xs leading-snug">
                Aún no se ha generado una firma criptográfica para este embarque.
                Descarga el Dossier DIAN para sellar el expediente.
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* ── Signature metadata grid ─────────────────────────── */}
            <div className="rounded-md border bg-muted/10 divide-y">
              <MetaRow label="Hash raíz del dossier">
                <div className="flex items-center gap-1.5">
                  <code className="font-mono text-[10px] text-foreground tabular-nums">
                    {signature.dossier_root_hash.slice(0, 12)}…
                  </code>
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => copyHash(signature.dossier_root_hash)}
                          className="p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                        >
                          <Copy className="h-3 w-3" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-[10px] font-mono max-w-[420px] break-all">
                        {signature.dossier_root_hash}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </MetaRow>
              <MetaRow label="Sellado el">
                <span className="text-[11px] tabular-nums text-foreground">
                  {formatDateTime(signature.signed_at)}
                </span>
              </MetaRow>
              <MetaRow label="Llave criptográfica">
                <Badge variant="outline" className="font-mono text-[10px] h-5">
                  {signature.key_id}
                </Badge>
              </MetaRow>
              <MetaRow label="Algoritmo">
                <span className="text-[11px] font-mono text-muted-foreground">HMAC-SHA256</span>
              </MetaRow>
            </div>

            {/* ── Verify action ───────────────────────────────────── */}
            <div className="flex items-center justify-between gap-2 pt-1">
              <p className="text-[11px] text-muted-foreground leading-snug max-w-[60%]">
                Recalcula los hashes de los archivos en el almacenamiento y los
                compara con la firma original.
              </p>
              <Button
                onClick={handleVerify}
                disabled={isVerifying}
                size="sm"
                variant="outline"
                className="gap-2 shrink-0"
              >
                {isVerifying ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Verificando…
                  </>
                ) : (
                  <>
                    <FileCheck className="h-3.5 w-3.5" />
                    Verificar Integridad
                  </>
                )}
              </Button>
            </div>

            {/* ── Inline result semaphore ─────────────────────────── */}
            {status === "valid" && result && (
              <div className="rounded-md border border-emerald-600/30 bg-emerald-50 dark:bg-emerald-950/20 p-3 space-y-1">
                <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400 font-semibold text-[12px]">
                  <CheckCircle2 className="h-4 w-4" />
                  ✅ Integridad Confirmada
                </div>
                <p className="text-[10px] text-emerald-700/80 dark:text-emerald-400/80 pl-6 leading-snug">
                  Todos los archivos del dossier coinciden con la firma original.
                  El expediente no ha sido modificado.
                </p>
              </div>
            )}

            {status === "invalid" && result && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 space-y-2">
                <div className="flex items-center gap-2 text-destructive font-semibold text-[12px]">
                  <XCircle className="h-4 w-4" />
                  ❌ Firma Inválida — Posible Manipulación
                </div>
                <ul className="text-[10px] text-destructive/90 space-y-0.5 pl-6 list-disc">
                  {result.signature_match === false && (
                    <li>La firma HMAC no coincide con el manifest almacenado.</li>
                  )}
                  {result.manifest_internal_root_match === false && (
                    <li>El hash raíz del manifest es internamente inconsistente.</li>
                  )}
                  {result.live_root_matches_manifest_root === false && (
                    <li>Los archivos en el almacenamiento difieren del manifest firmado.</li>
                  )}
                </ul>
                {result.tampered_files && result.tampered_files.length > 0 && (
                  <div className="pl-6 pt-1">
                    <p className="text-[10px] font-semibold text-destructive mb-1">
                      Archivos alterados ({result.tampered_files.length}):
                    </p>
                    <ul className="space-y-0.5 max-h-32 overflow-y-auto">
                      {result.tampered_files.map((f, i) => (
                        <li
                          key={i}
                          className="text-[10px] font-mono text-destructive/90 truncate"
                        >
                          • {f.zip_path ?? f.storage_path ?? f.document_id ?? "archivo desconocido"}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {status === "error" && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3">
                <div className="flex items-center gap-2 text-destructive font-semibold text-[12px]">
                  <XCircle className="h-4 w-4" />
                  Error al verificar
                </div>
                <p className="text-[10px] text-destructive/90 pl-6 mt-1">
                  {error ?? "No se pudo contactar el servicio de verificación."}
                </p>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function MetaRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between px-3 py-2">
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
        {label}
      </span>
      <div className="flex items-center">{children}</div>
    </div>
  );
}

function formatDateTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-CO", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
